import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchQuotations,
  createQuotation,
  updateQuotation,
  sendQuotation,
  cancelQuotation,
  fetchAssignedPlots,
  fetchBillingCategories,
  fetchQuotationHistory,
  downloadQuotation,
  fetchQuotationPdfUrl,
  fetchZohoItems,
} from "../api/api";

const STATUS_LABELS = {
  draft: "Draft",
  proposed: "Sent — awaiting customer",
  accepted: "Accepted",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

const EMPTY_LINE_ITEM = { name: "", description: "", quantity: 1, rate: "" };

const EMPTY_FORM = {
  plot_ids: [],
  category_id: "",
  quotation_date: new Date().toISOString().slice(0, 10),
  expiry_date: "",
  subject: "",
  customer_notes: "",
  terms_conditions: "",
  discount_percent: 0,
  tax_amount: 0,
  adjustment: 0,
  line_items: [{ ...EMPTY_LINE_ITEM }],
};

function Quotations() {

  const [quotations, setQuotations] = useState([]);
  const [plots, setPlots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [actioningType, setActioningType] = useState(null); // "send" | "cancel"
  const [plotSearch, setPlotSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [historyByQuotationId, setHistoryByQuotationId] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [zohoItems, setZohoItems] = useState([]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [quotationData, plotData, categoryData] = await Promise.all([
        fetchQuotations(),
        fetchAssignedPlots(),
        fetchBillingCategories(),
      ]);
      // Best-effort — the Items picker is a convenience, not required to
      // use the form, so a failure here (e.g. Zoho Books not configured)
      // shouldn't block loading the page.
      fetchZohoItems().then(setZohoItems).catch(() => {});
      setQuotations(quotationData);
      setPlots(plotData);
      setCategories(categoryData);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePlot = (plotId) => {
    setForm((prev) => {
      const plot_ids = prev.plot_ids.includes(plotId)
        ? prev.plot_ids.filter((id) => id !== plotId)
        : [...prev.plot_ids, plotId];
      return { ...prev, plot_ids };
    });
  };

  const handleLineItemChange = (index, field, value) => {
    setForm((prev) => {
      const line_items = prev.line_items.slice();
      line_items[index] = { ...line_items[index], [field]: value };
      return { ...prev, line_items };
    });
  };

  // Picking a Zoho Item auto-fills its saved description/rate onto the row,
  // same as selecting one directly in Zoho Books' own New Estimate screen —
  // the fields stay freely editable afterwards (e.g. the maintenance period
  // dates inside the description).
  const handlePickZohoItem = (index, zohoItemId) => {
    const item = zohoItems.find((it) => it.zoho_item_id === zohoItemId);
    if (!item) return;
    setForm((prev) => {
      const line_items = prev.line_items.slice();
      line_items[index] = {
        ...line_items[index],
        name: item.name,
        description: item.description || item.name,
        rate: item.rate || line_items[index].rate,
      };
      return { ...prev, line_items };
    });
  };

  const addLineItem = () => {
    setForm((prev) => ({ ...prev, line_items: [...prev.line_items, { ...EMPTY_LINE_ITEM }] }));
  };

  const removeLineItem = (index) => {
    setForm((prev) => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }));
  };

  const lineItemsTotal = form.line_items.reduce(
    (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.rate) || 0), 0
  );
  // Live preview of the same formula the backend computes on save
  // (_compute_totals in quotations.py) — updates as any of these fields
  // change, same as Zoho Books' own live-updating Total on the Estimate
  // screen.
  const discountAmount = lineItemsTotal * ((Number(form.discount_percent) || 0) / 100);
  const liveTotal = lineItemsTotal - discountAmount + (Number(form.tax_amount) || 0) + (Number(form.adjustment) || 0);

  const plotSearchQuery = plotSearch.trim().toLowerCase();
  const filteredPlots = plotSearchQuery
    ? plots.filter((p) => `${p.plot_number} ${p.owner_name || ""}`.toLowerCase().includes(plotSearchQuery))
    : plots;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.plot_ids.length === 0) {
      setError("Select at least one plot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        plot_ids: form.plot_ids,
        category_id: form.category_id ? Number(form.category_id) : null,
        quotation_date: form.quotation_date,
        expiry_date: form.expiry_date || null,
        subject: form.subject || null,
        customer_notes: form.customer_notes || null,
        terms_conditions: form.terms_conditions || null,
        discount_percent: Number(form.discount_percent) || 0,
        tax_amount: Number(form.tax_amount) || 0,
        adjustment: Number(form.adjustment) || 0,
        line_items: form.line_items
          .filter((li) => li.description && li.rate !== "")
          .map((li) => ({ name: li.name || null, description: li.description, quantity: Number(li.quantity) || 1, rate: Number(li.rate) })),
      };
      if (editingId) {
        await updateQuotation(editingId, payload);
      } else {
        await createQuotation(payload);
      }
      setForm(EMPTY_FORM);
      setPlotSearch("");
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (quotation) => {
    setEditingId(quotation.id);
    setForm({
      plot_ids: [quotation.plot_id, ...(quotation.additional_plot_ids || [])],
      category_id: quotation.category_id || "",
      quotation_date: quotation.quotation_date,
      expiry_date: quotation.expiry_date || "",
      subject: quotation.subject || "",
      customer_notes: quotation.customer_notes || "",
      terms_conditions: quotation.terms_conditions || "",
      discount_percent: quotation.discount_percent || 0,
      tax_amount: quotation.tax_amount || 0,
      adjustment: quotation.adjustment || 0,
      line_items: quotation.line_items.length > 0
        ? quotation.line_items.map((li) => ({ name: li.name || "", description: li.description, quantity: li.quantity ?? 1, rate: li.rate ?? li.amount }))
        : [{ ...EMPTY_LINE_ITEM }],
    });
    setPlotSearch("");
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setForm(EMPTY_FORM);
    setPlotSearch("");
    setShowForm(false);
    setEditingId(null);
  };

  const handleSend = async (id) => {
    setActioningId(id);
    setActioningType("send");
    setError("");
    try {
      await sendQuotation(id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setActioningId(null);
      setActioningType(null);
    }
  };

  const handleCancel = async (id) => {
    setActioningId(id);
    setActioningType("cancel");
    setError("");
    try {
      await cancelQuotation(id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setActioningId(null);
      setActioningType(null);
    }
  };

  const handleToggleHistory = async (quotationId) => {
    if (expandedId === quotationId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(quotationId);
    if (historyByQuotationId[quotationId]) return;
    setHistoryLoadingId(quotationId);
    try {
      const data = await fetchQuotationHistory(quotationId);
      setHistoryByQuotationId((prev) => ({ ...prev, [quotationId]: data }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const handleDownload = async (quotation) => {
    setDownloadingId(quotation.id);
    setError("");
    try {
      await downloadQuotation(quotation.id, quotation.quotation_number);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewPdf = async (quotation) => {
    setPdfLoadingId(quotation.id);
    setError("");
    try {
      const url = await fetchQuotationPdfUrl(quotation.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const filteredQuotations = statusFilter
    ? quotations.filter((q) => q.status === statusFilter)
    : quotations;

  return (
    <div>
      <Navbar />
      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>Estimates</h1>
          <button
            className="login-btn"
            style={{ width: "auto", padding: "10px 20px" }}
            onClick={() => (showForm ? handleCancelForm() : setShowForm(true))}
          >
            {showForm ? "Cancel" : "+ New Estimate"}
          </button>
        </div>

        <p style={{ color: "#666" }}>
          Draft an estimate here, then click Send to push it to Zoho Books as an estimate
          and email it to the customer directly — no need to also send it from inside Zoho
          Books. It then becomes visible to the customer to Accept or Reject.
        </p>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        {showForm && (
          <form onSubmit={handleSubmit} style={{ background: "white", borderRadius: "10px", padding: "20px", marginBottom: "20px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <div style={{ marginBottom: "12px" }}>
              <label>Plot(s) — select one or more; the same line items are applied to each</label>
              <input
                type="text"
                placeholder="Search by plot number or customer name"
                className="search-bar"
                value={plotSearch}
                onChange={(e) => setPlotSearch(e.target.value)}
                style={{ marginBottom: "6px" }}
              />
              <div style={{ maxHeight: "160px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "8px", padding: "8px" }}>
                {filteredPlots.length === 0 && <p style={{ color: "#888", margin: 0 }}>No plots match "{plotSearch.trim()}".</p>}
                {filteredPlots.map((p) => (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0", fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={form.plot_ids.includes(p.id)}
                      onChange={() => togglePlot(p.id)}
                    />
                    {p.plot_number} — {p.owner_name || "Unassigned"}
                  </label>
                ))}
              </div>
              {form.plot_ids.length > 0 && (
                <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                  {form.plot_ids.length} plot(s) selected — one estimate will be created covering all of them (the total below is for the estimate, not per plot).
                </p>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label>Category</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Estimate Date</label>
                <input type="date" required value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} />
              </div>
              <div>
                <label>Expiry Date</label>
                <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label>Subject</label>
              <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Let the customer know what this is for" />
            </div>

            <h4>Line Items</h4>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr auto", gap: "8px", marginBottom: "4px" }}>
              <label style={{ fontSize: "12px", color: "#666" }}>Item Name &amp; Description</label>
              <label style={{ fontSize: "12px", color: "#666" }}>Qty</label>
              <label style={{ fontSize: "12px", color: "#666" }}>Rate</label>
              <span />
            </div>
            {form.line_items.map((li, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr auto", gap: "8px", marginBottom: "8px" }}>
                <div>
                  {zohoItems.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => handlePickZohoItem(i, e.target.value)}
                      style={{ marginBottom: "4px", fontSize: "12px", color: "#666" }}
                    >
                      <option value="">Pick a saved item… (optional)</option>
                      {zohoItems.map((item) => (
                        <option key={item.zoho_item_id} value={item.zoho_item_id}>{item.name}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    placeholder="Item Name"
                    value={li.name}
                    onChange={(e) => handleLineItemChange(i, "name", e.target.value)}
                    style={{ fontWeight: 700, marginBottom: "4px" }}
                  />
                  <input type="text" placeholder="Description" required value={li.description} onChange={(e) => handleLineItemChange(i, "description", e.target.value)} />
                </div>
                <input type="number" required value={li.quantity} onChange={(e) => handleLineItemChange(i, "quantity", e.target.value)} />
                <input type="number" placeholder="Rate" required value={li.rate} onChange={(e) => handleLineItemChange(i, "rate", e.target.value)} />
                <button type="button" className="register-btn" style={{ width: "auto", padding: "6px 10px" }} onClick={() => removeLineItem(i)} disabled={form.line_items.length === 1}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="register-btn" style={{ width: "auto", padding: "6px 12px", marginBottom: "12px" }} onClick={addLineItem}>
              + Add Line
            </button>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
              <div>
                <label>Discount %</label>
                <input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} />
              </div>
              <div>
                <label>Tax Amount</label>
                <input type="number" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} />
              </div>
              <div>
                <label>Adjustment</label>
                <input type="number" value={form.adjustment} onChange={(e) => setForm({ ...form, adjustment: e.target.value })} placeholder="+/- e.g. rounding" />
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label>Customer Notes</label>
              <textarea rows={2} value={form.customer_notes} onChange={(e) => setForm({ ...form, customer_notes: e.target.value })} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label>Terms &amp; Conditions</label>
              <textarea rows={2} value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} />
            </div>

            <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Subtotal</span><span>{formatCurrency(lineItemsTotal)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Discount ({Number(form.discount_percent) || 0}%)</span><span>- {formatCurrency(discountAmount)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Tax Amount</span><span>{formatCurrency(Number(form.tax_amount) || 0)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                <span>Adjustment</span><span>{formatCurrency(Number(form.adjustment) || 0)}</span>
              </p>
              <p style={{ display: "flex", justifyContent: "space-between", margin: "8px 0 0", paddingTop: "8px", borderTop: "1px solid #ddd" }}>
                <strong>Total</strong><strong>{formatCurrency(liveTotal)}</strong>
              </p>
            </div>

            <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} disabled={submitting}>
              {submitting ? "Saving…" : editingId ? "Save Changes" : "Save as Draft"}
            </button>
          </form>
        )}

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {loading && <p>Loading estimates…</p>}
        {!loading && filteredQuotations.length === 0 && <p>No estimates match these filters.</p>}

        {!loading && filteredQuotations.length > 0 && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Estimate #</th>
                  <th>Plot</th>
                  <th>Customer</th>
                  <th>Category</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotations.map((q) => (
                  <React.Fragment key={q.id}>
                  <tr>
                    <td>{q.quotation_number}</td>
                    <td>{(q.all_plot_numbers && q.all_plot_numbers.length > 0) ? q.all_plot_numbers.join(", ") : (q.plot_number || "—")}</td>
                    <td>{q.owner_name || "—"}</td>
                    <td>{q.category_name || "—"}</td>
                    <td>{formatCurrency(q.total)}</td>
                    <td>
                      {STATUS_LABELS[q.status] || q.status}
                      {q.status === "draft" && q.zoho_sync_error && (
                        <div style={{ color: "red", fontWeight: 400, fontSize: "12px", marginTop: "4px" }}>{q.zoho_sync_error}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {q.status === "draft" && (
                          <>
                            <button
                              className="register-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleEditClick(q)}
                            >
                              Edit
                            </button>
                            <button
                              className="login-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleSend(q.id)}
                              disabled={actioningId === q.id}
                            >
                              {actioningId === q.id && actioningType === "send" ? "Sending…" : "Send"}
                            </button>
                            <button
                              className="register-btn"
                              style={{ width: "auto", padding: "6px 12px" }}
                              onClick={() => handleCancel(q.id)}
                              disabled={actioningId === q.id}
                            >
                              {actioningId === q.id && actioningType === "cancel" ? "Cancelling…" : "Cancel"}
                            </button>
                          </>
                        )}
                        {q.status === "draft" && (
                          <button
                            className="register-btn"
                            style={{ width: "auto", padding: "6px 12px" }}
                            onClick={() => handleDownload(q)}
                            disabled={downloadingId === q.id}
                          >
                            {downloadingId === q.id ? "Opening…" : "View"}
                          </button>
                        )}
                        {q.pdf_available && (
                          <button
                            className="register-btn"
                            style={{ width: "auto", padding: "6px 12px" }}
                            onClick={() => handleViewPdf(q)}
                            disabled={pdfLoadingId === q.id}
                          >
                            {pdfLoadingId === q.id ? "Opening…" : "View Estimate PDF"}
                          </button>
                        )}
                        <button
                          className="register-btn"
                          style={{ width: "auto", padding: "6px 12px" }}
                          onClick={() => handleToggleHistory(q.id)}
                        >
                          {expandedId === q.id ? "Hide History" : "History"}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === q.id && (
                    <tr>
                      <td colSpan={7} style={{ background: "#fafafa", padding: "12px 16px" }}>
                        {historyLoadingId === q.id && <p style={{ margin: 0 }}>Loading history…</p>}
                        {historyLoadingId !== q.id && (historyByQuotationId[q.id] || []).length === 0 && (
                          <p style={{ margin: 0, color: "#888" }}>No status changes recorded yet.</p>
                        )}
                        {historyLoadingId !== q.id && (historyByQuotationId[q.id] || []).map((h) => (
                          <div key={h.id} style={{ padding: "6px 0", borderBottom: "1px solid #eee", fontSize: "13px" }}>
                            <strong>{STATUS_LABELS[h.from_status] || h.from_status || "—"} → {STATUS_LABELS[h.to_status] || h.to_status}</strong>
                            {" "}<span style={{ color: "#888" }}>({h.source}{h.changed_by_name ? ` — ${h.changed_by_name}` : ""})</span>
                            {" "}<span style={{ color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                            {h.reason && <div style={{ color: "#444", marginTop: "2px" }}>{h.reason}</div>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
}

export default Quotations;
